import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./HeaderSection.module.scss";
import { LinkedInLettersIcon } from "@/frontend/shared/assets/icons/LinkedInLettersIcon";
import clsx from "clsx";
import { refactorWebsiteLink } from "@/frontend/shared/lib/refactorWebsiteLink/refactorWebsiteLink";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { TagTabV2 } from "@/frontend/shared/ui/TagTabV2";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { WebIcon } from "@/frontend/shared/assets/icons/WebIcon";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { CommentIcon } from "@/frontend/shared/assets/icons/CommentIcon";
import { EActiveView, TCompanyData } from "@/frontend/features/Company/model/types/types";

export function HeaderSection({
  data,
  activeView,
  onChangeActiveView,
  isContainerScrolled,
  setIsCommentsSectionOpen,
  showComments
}: {
  data?: TCompanyData;
  activeView: EActiveView;
  onChangeActiveView: (view: EActiveView) => void;
  companyId?: number;
  triggerRefetch: () => void;
  refetchDetailsData: () => void;
  isContainerScrolled?: boolean;
  setIsCommentsSectionOpen?: (value: boolean) => void;
  showComments?: boolean;
}) {
  if (!data) return null;

  const { name, website, logoUrl, headline, linkedInUrl, harmonicTags } = data;

  const isMobile = useScreenWidth() <= 768;

  function displayedWebsiteField() {
    const content = website ? (
      <div className={styles.websiteContainerBlock}>
        <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
          <a href={refactorWebsiteLink(website)} target="_blank" rel="noreferrer" className={styles.websiteContainer}>
            <WebIcon />
            <Typography variant="label-5" color="greyscale700">
              {website}
            </Typography>
          </a>
        </NormalButton>
      </div>
    ) : null;

    return content;
  }

  return (
    <div className={styles.container}>
      <div className={styles.block}>
        <div className={clsx(styles.upperContent, !isContainerScrolled && styles.minimizedUpperContent)}>
          <div className={styles.logoContainer}>
            {logoUrl ? (
              <img src={logoUrl} alt={name || "logo"} className={styles.logo} />
            ) : (
              <div className={styles.logoPlaceholder} />
            )}
          </div>
          <div className={styles.infoContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>
                <Typography variant="label-1" color="greyscale900">
                  {name}
                </Typography>
              </div>
              <div className={styles.info}>
                {displayedWebsiteField()}
                <div className={styles.iconsContainer}>
                  {linkedInUrl && (
                    <a href={linkedInUrl} target="_blank" rel="noreferrer" className={styles.linkedinIconContainer}>
                      <LinkedInLettersIcon className={styles.linkedinIcon} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div
              className={clsx(styles.descriptionBlock, !isContainerScrolled && isMobile && styles.hiddenDescription)}
            >
              <div className={styles.subDescription}>
                <Typography variant="body-2" color="greyscale800" className={styles.shortDescription}>
                  {headline}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(styles.tagsContainerBlock, !isContainerScrolled && styles.hiddenTagsContainer)}>
          <div className={clsx(styles.tagsContainer)}>
            {!!harmonicTags && harmonicTags.length > 0 && (
              <div className={styles.innoveraTagsContainer}>
                <TagTabV2 customTags={false}>
                  {harmonicTags?.map((tag: string, index: number) => {
                    return <Tag children={tag} type="innovera" key={index} className={styles.tag} />;
                  })}
                </TagTabV2>
              </div>
            )}
          </div>
        </div>
        <div className={styles.bottomContent}>
          <div className={styles.viewSwitcherContainer}>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.PROFILE)}
              className={clsx(styles.option, activeView === EActiveView.PROFILE && styles.active)}
            >
              Profile
            </Typography>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.DATA_ROOM)}
              className={clsx(styles.option, activeView === EActiveView.DATA_ROOM && styles.active)}
            >
              Data Room
            </Typography>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.ANNOTATIONS)}
              className={clsx(styles.option, activeView === EActiveView.ANNOTATIONS && styles.active)}
            >
              Notes
            </Typography>
          </div>
          {!showComments && !!setIsCommentsSectionOpen && (
            <NormalButton variant="tertiary" size="large" onClick={() => setIsCommentsSectionOpen(true)}>
              <CommentIcon />
            </NormalButton>
          )}
        </div>
      </div>
    </div>
  );
}
